import PropTypes from 'prop-types';

import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import FDPersonComponent from '../FDPersonComponent';
import DeliveryComponent from './DeliveryComponent';

import { DeliverySignOutSkeleton } from '../../Shared/skeletons';

const StyledTitle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSpacer',
})(({ theme, isSpacer }) => ({
  fontSize: '1.8rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
  [theme.breakpoints.up('md')]: {
    marginLeft: '25rem',
  },
  ...(isSpacer && {
    marginTop: '3rem',
  }),
}));

const StyledResidentContainer = styled(Box)(({ theme }) => ({
  margin: '3rem 0',
  '& section': {
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
}));

const DeliverySignOutComponent = ({
  unit,
  unitDeliveries,
  unitResidents,
  isLoading,
  checkedDeliveries,
  onDeliverySignOutChange,
  onDeliverySignOut,
  isSelfSignOut,
}) => {
  const { t } = useTransl();
  const { name, parentProperty, recipientId } = unit;

  if (isLoading) return <DeliverySignOutSkeleton />;

  if (!unitResidents?.length)
    return (
      <Typography fontWeight="bold" textAlign="center">
        {t('delivery.noSignOut')}
      </Typography>
    );

  const deliveriesCount = unitDeliveries?.length;
  const title = isSelfSignOut
    ? t('delivery.countToSignOut', {
        deliveriesCount,
        name,
      })
    : t('delivery.selectToSignOut');

  return (
    <Box>
      <StyledTitle>{title}</StyledTitle>
      {unitResidents?.map((resident, i) => {
        const { id, isUnitDelivery } = resident;
        const profileUrl = isSelfSignOut
          ? null
          : `/building/${parentProperty}/residents/${id}`;

        const residentDeliveries = unitDeliveries.filter(
          ({ recipient }) => recipient?.id === id,
        );
        if (!residentDeliveries?.length) return null;
        return (
          <StyledResidentContainer key={id || i}>
            <section>
              <Box
                sx={(theme) => ({
                  width: '25rem',
                  [theme.breakpoints.down('md')]: {
                    width: '100%',
                  },
                })}
              >
                {isUnitDelivery ? (
                  <Typography fontWeight="bold">
                    {t('delivery.unitDelivery')}
                  </Typography>
                ) : (
                  <FDPersonComponent
                    resident={{ ...resident, profileUrl }}
                    buildingId={parentProperty}
                  />
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  flex: 1,
                }}
              >
                {residentDeliveries?.map((delivery) => (
                  <DeliveryComponent
                    key={delivery?.id}
                    isChecked={checkedDeliveries.includes(delivery?.id)}
                    delivery={delivery}
                    onDeliverySignOutChange={onDeliverySignOutChange}
                    isSelfSignOut={isSelfSignOut}
                  />
                ))}
              </Box>
            </section>

            {recipientId && recipientId === id && unitResidents.length > 1 && (
              <StyledTitle isSpacer>{t('delivery.moreNote')}</StyledTitle>
            )}
          </StyledResidentContainer>
        );
      })}
      <Box sx={{ margin: '2rem 0' }}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          onClick={onDeliverySignOut}
          disabled={!checkedDeliveries?.length}
          disableElevation
          fullWidth
        >
          {t('delivery.signOut')}
        </Button>
      </Box>
    </Box>
  );
};

DeliverySignOutComponent.propTypes = {
  unit: PropTypes.instanceOf(Object).isRequired,
  unitDeliveries: PropTypes.instanceOf(Array).isRequired,
  unitResidents: PropTypes.instanceOf(Array).isRequired,
  checkedDeliveries: PropTypes.instanceOf(Array).isRequired,
  isLoading: PropTypes.bool.isRequired,
  onDeliverySignOutChange: PropTypes.func.isRequired,
  onDeliverySignOut: PropTypes.func.isRequired,
  isSelfSignOut: PropTypes.bool,
};

export default DeliverySignOutComponent;
